import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MODULE_DEFS } from "../lib/modules";
import { LayoutDashboard, History, CreditCard, LogOut, Menu, Users, ShieldCheck, Sparkles, FileLock, Camera, Network, AlertTriangle, ExternalLink, Activity, FileText, Receipt, BarChart3, Database, Lock, ChevronDown, Settings, BookOpen, MessageCircle, Info, Clock } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import Footer from "./Footer";
import QuotaBanner from "./QuotaBanner";
import ChameleonIcon from "./ChameleonIcon";
import DisclaimerBanner from "./DisclaimerBanner";
import DisclaimerFooterBar from "./DisclaimerFooterBar";
import { isClinicalPath } from "../lib/disclaimer";
import useIsMobile from "../lib/useIsMobile";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const showClinicalBanner = isClinicalPath(location.pathname);
  const isMobile = useIsMobile();

  // Close profile menu on outside click / escape
  useEffect(() => {
    if (!profileOpen) return;
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setProfileOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileOpen]);

  const navItem = (to, label, Icon) => (
    <NavLink
      key={to}
      to={to}
      end={to === "/dashboard" || to === "/quick-assessment"}
      onClick={() => setOpen(false)}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 lg:py-2 min-h-[44px] lg:min-h-0 rounded-md text-[15px] lg:text-sm transition-colors ${
          isActive
            ? "bg-[var(--pa-surface)] text-[var(--pa-gold)] border-l-2 border-[var(--pa-gold)]"
            : "text-[var(--pa-text-muted)] hover:text-[var(--pa-text)] hover:bg-[var(--pa-surface)]/50 border-l-2 border-transparent"
        }`
      }
    >
      {Icon && <Icon size={16} />}
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--pa-bg)" }}>
      <div className="flex flex-1">
        {/* Backdrop for mobile sidebar */}
        {open && (
          <div
            data-testid="sidebar-backdrop"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
            aria-hidden="true"
          />
        )}
        {/* Sidebar */}
        <aside
          className={`${
            open ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 fixed lg:sticky top-0 left-0 z-50 lg:z-auto w-72 shrink-0 border-r border-[var(--pa-border)] bg-[var(--pa-bg-2)] h-screen overflow-y-auto transition-transform duration-200 ease-out`}
          data-testid="sidebar"
        >
          <div className="p-6 border-b border-[var(--pa-border)]">
            <div className="flex items-center gap-2.5">
              <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-link">
                <ChameleonIcon size={24} className="text-[var(--pa-gold)]" />
                <div className="flex items-baseline gap-1.5">
                  <span className="font-serif text-2xl text-[var(--pa-text)]">Prostho</span>
                  <span className="font-serif text-2xl text-[var(--pa-gold)]">AI</span>
                </div>
              </Link>
            </div>
            <a
              href="https://camelionai.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brand-camelionai"
              className="mt-1 ml-9 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[var(--pa-text-dim)] hover:text-[var(--pa-gold)] transition w-fit"
            >
              by CamelionAI <ExternalLink size={9} />
            </a>
            <div className="kicker mt-3">Clinical Decision Support</div>
          </div>

          <nav className="p-4 space-y-1">
            {isMobile ? (
              /* ────────────────────────────────────────────────────────
                 Mobile PWA Batch B3 — streamlined smartphone nav.
                 Only Ask the Bench, Case Intake, Account, Sign out.
                 Preview / no-PHI notice is rendered below.
                 ──────────────────────────────────────────────────────── */
              <>
                <div className="kicker pb-2 px-3" data-testid="mobile-nav-header">Chairside</div>
                {navItem("/quick-assist", "Ask the Bench", MessageCircle)}
                {navItem("/quick-assessment/new", "Case Intake", Camera)}
                {navItem("/quick-assessment", "Recent Cases", Clock)}
                <div className="kicker pt-6 pb-2 px-3">Account</div>
                {navItem("/account", "Account & Security", Settings)}
                <button
                  onClick={() => {
                    setOpen(false);
                    nav("/", { replace: true });
                    logout();
                  }}
                  data-testid="mobile-nav-signout"
                  className="w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-md text-[15px] text-red-300 hover:bg-red-500/10 border-l-2 border-transparent transition-colors"
                >
                  <LogOut size={16} />
                  <span>Sign out</span>
                </button>

                {/* Preview / no-PHI notice — mobile chairside */}
                <div
                  data-testid="mobile-nav-preview-notice"
                  className="mt-6 mx-1 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-start gap-2"
                >
                  <Info size={14} className="text-amber-300 mt-0.5 shrink-0" />
                  <div className="text-[11px] text-amber-100 leading-snug">
                    <strong className="text-amber-200">Preview — no PHI.</strong> Do not enter
                    real patient identifiers. Chairside workflows only; full clinical modules
                    are available on desktop or tablet.
                  </div>
                </div>
              </>
            ) : (
              <>
                {navItem("/dashboard", "Dashboard", LayoutDashboard)}
                {navItem("/quick-assessment/new", "Quick Assessment", Camera)}
                {navItem("/case-analysis/new", "Complete Case Analysis", Sparkles)}
                {navItem("/patients", "Patients", Users)}
                {navItem("/collab-cases", "Interdisciplinary Cases", Network)}
                {navItem("/history", "Case History", History)}
                {navItem("/plans-membership", "Plans & Membership", CreditCard)}
                {navItem("/account", "Account", CreditCard)}
                {user?.is_admin && navItem("/admin/reviews", "Review Queue", ShieldCheck)}
                {user?.is_admin && navItem("/admin/red-flags", "Red Flag Ledger", AlertTriangle)}
                {user?.is_admin && navItem("/admin/audit-logs", "Audit Logs", FileLock)}
                {user?.is_admin && navItem("/security-admin", "Security Ops", Lock)}

                <div className="kicker pt-6 pb-2 px-3">Diagnostics &amp; Planning</div>
                {navItem("/topology-analysis/new", "Topology Analysis", Network)}
                {navItem("/tmd-assessment/new", "TMD Assessment", Activity)}
                {navItem("/perio-prostho/new", "Perio-Prostho Planner", Activity)}
                {navItem("/treatment-plan/new", "Treatment Plan Engine", FileText)}

                <div className="kicker pt-6 pb-2 px-3">Business Intelligence</div>
                {navItem("/insurance-fees", "Insurance &amp; Fees", Database)}
                {navItem("/claims", "Claims &amp; Billing", Receipt)}
                {navItem("/analytics", "Analytics", BarChart3)}

                <div className="kicker pt-6 pb-2 px-3">Clinical Modules</div>
                {Object.entries(MODULE_DEFS).map(([slug, def]) => {
                  const Icon = def.Icon;
                  return (
                    <NavLink
                      key={slug}
                      to={`/module/${slug}`}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-module-${slug}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 lg:py-2 min-h-[44px] lg:min-h-0 rounded-md text-[14px] lg:text-xs transition-colors ${
                          isActive
                            ? "bg-[var(--pa-surface)] text-[var(--pa-gold)] border-l-2 border-[var(--pa-gold)]"
                            : "text-[var(--pa-text-muted)] hover:text-[var(--pa-text)] hover:bg-[var(--pa-surface)]/50 border-l-2 border-transparent"
                        }`
                      }
                    >
                      <Icon size={14} />
                      <span className="truncate">{def.name}</span>
                    </NavLink>
                  );
                })}
              </>
            )}
          </nav>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="h-16 border-b border-[var(--pa-border)] flex items-center justify-between px-4 md:px-8 bg-[var(--pa-bg-2)]">
            <button
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition-colors tap-target -ml-2"
              data-testid="mobile-menu-toggle"
              aria-label="Open navigation menu"
              aria-expanded={open}
            >
              <Menu size={22} />
            </button>
            <div className="flex-1" />
            <div className="flex items-center gap-3" ref={profileRef}>
              {/* Profile dropdown trigger */}
              <button
                onClick={() => setProfileOpen((o) => !o)}
                data-testid="profile-dropdown-trigger"
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                className="flex items-center gap-2 text-right rounded-md px-2 py-1.5 hover:bg-[var(--pa-surface)] transition-colors"
              >
                <div className="hidden sm:block">
                  <div className="text-sm text-[var(--pa-text)] leading-tight">{user?.full_name}</div>
                  <div className="text-[10px] text-[var(--pa-text-dim)] leading-tight">
                    {user?.is_admin
                      ? "ADMIN · Unmetered"
                      : user?.subscription_status === "active"
                      ? `${user.subscription_plan?.toUpperCase()} · Active`
                      : "Free trial"}
                  </div>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-[var(--pa-text-muted)] transition-transform ${profileOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Dropdown menu */}
              {profileOpen && (
                <div
                  data-testid="profile-dropdown-menu"
                  role="menu"
                  className="absolute right-2 sm:right-4 md:right-8 top-14 z-40 w-[calc(100vw-1rem)] sm:w-64 max-w-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md shadow-2xl shadow-black/50 py-1.5"
                >
                  <div className="px-3 py-2 border-b border-[var(--pa-border)] text-[10px] text-[var(--pa-text-dim)]" data-testid="profile-dropdown-email">
                    Signed in as <span className="text-[var(--pa-text-muted)]">{user?.email}</span>
                  </div>
                  <button
                    onClick={() => { setProfileOpen(false); nav("/account"); }}
                    data-testid="profile-dropdown-account"
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--pa-text)] hover:bg-[var(--pa-surface)] transition text-left"
                    role="menuitem"
                  >
                    <Settings size={14} className="text-[var(--pa-gold)]" />
                    Account & ARIA settings
                  </button>
                  <Link
                    to="/legal/disclaimer"
                    onClick={() => setProfileOpen(false)}
                    data-testid="profile-dropdown-reread-disclaimer"
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--pa-text)] hover:bg-[var(--pa-surface)] transition"
                    role="menuitem"
                  >
                    <BookOpen size={14} className="text-[var(--pa-gold)]" />
                    Re-read disclaimer
                  </Link>
                  <a
                    href="/baa"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setProfileOpen(false)}
                    data-testid="profile-dropdown-baa"
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--pa-text)] hover:bg-[var(--pa-surface)] transition"
                    role="menuitem"
                  >
                    <FileText size={14} className="text-[var(--pa-gold)]" />
                    Business Associate Agreement
                  </a>
                  <div className="my-1 border-t border-[var(--pa-border)]" />
                  <button
                    onClick={() => {
                      setProfileOpen(false);
                      nav("/", { replace: true });
                      logout();
                    }}
                    data-testid="profile-dropdown-logout"
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-300 hover:bg-red-500/10 transition text-left"
                    role="menuitem"
                  >
                    <LogOut size={14} />
                    Sign out
                  </button>
                </div>
              )}

              {/* Compact icon-only logout for the desktop header (kept for muscle memory) */}
              <button
                onClick={() => {
                  nav("/", { replace: true });
                  logout();
                }}
                data-testid="logout-button"
                className="hidden sm:inline-flex tap-target text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition-colors"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut size={18} />
              </button>
            </div>
          </header>

          <QuotaBanner />

          <main className="flex-1 p-4 md:p-8" data-testid="main-content">
            {showClinicalBanner && <DisclaimerBanner />}
            {children}
          </main>

          <DisclaimerFooterBar />
          <Footer />
        </div>
      </div>
    </div>
  );
}
